<template>
  <ResponsiveLayout :progress="progress">
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>
    <!-- disable autocomplete here due to bfarm requirement, see
    https://developer.mozilla.org/en-US/docs/Web/Security/Practical_implementation_guides/Turning_off_form_autocompletion
    -->
    <form
      autocomplete="off"
      class="kds-main-wrapper"
      data-qa="sign-up"
      novalidate
    >
      <div class="kds-content-block">
        <p
          class="kds-typography-title-xlarge"
          data-qa="secondary-header"
        >
          {{ title }}
        </p>
      </div>
      <BaseBanner
        v-if="showLoginButton"
        scroll-into-view
        variant="important"
      >
        <p v-html="alreadyHaveAccount" />
        <template #actions>
          <BaseButton
            data-qa="login"
            @click="redirectToLogin"
          >
            {{ alreadyHaveAccountButton }}
          </BaseButton>
        </template>
      </BaseBanner>
      <BaseBanner
        v-if="errorMessage.show"
        :title="errorMessage.title"
        scroll-into-view
        variant="warning"
      >
        <p v-html="errorMessage.text" />
      </BaseBanner>
      <div class="kds-content-block">
        <TextField
          v-if="!featureFlagStore.flags.webOptimizedFunnel"
          :label="$t('eu__generic_name')"
          autocomplete="given-name"
          data-qa="first-name"
          v-bind="fieldAttrs.firstName"
        />
        <TextField
          :label="$t('eu__generic_email')"
          autocomplete="email"
          data-qa="email"
          type="email"
          v-bind="fieldAttrs.email"
        />
        <PasswordField
          autocomplete="new-password"
          data-qa="password"
          label="eu__generic_password"
          show-password-requirements
          v-bind="fieldAttrs.password"
        />
        <SelectField
          :label="$t('eu__signup__hdyhau__label')"
          :options="howDidYouHearAboutUsOptions"
          data-qa="how-did-you-hear-about-us"
          v-bind="fieldAttrs.howDidYouHearAboutUs"
        />
      </div>
      <div class="kds-content-block">
        <p class="kds-typography-paragraph-small">
          <DocumentViewer :link="consentFootnote" />
        </p>
      </div>
    </form>
    <template #pre_actions>
      <TrustSignal
        :icon="mdrIcon"
        :text="$t('trust_signal__mdr')"
      />
    </template>
    <template #actions>
      <BaseButton
        :text="$t('eu__sign_up__primary_cta_v9.5.0')"
        data-qa="submit"
        v-bind="submitAttrs"
      />
      <DocumentViewer>
        <template #default="{ viewDocument }">
          <nav
            class="legal-links kds-typography-paragraph-small"
            data-qa="legal-links"
          >
            <a
              href="#"
              data-custom="false"
              data-qa="imprint-link"
              :data-link="staticLinks.contact"
              @click="viewDocument"
            >
              {{ $t('eu__generic_imprint') }}
            </a>
          </nav>
        </template>
      </DocumentViewer>
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import useUserStore from '@shared/stores/useUserStore.js'
import useEuStore from '@eu/app/stores/useEuStore.js'
import { useI18n } from 'petite-vue-i18n'
import { useRouter } from 'vue-router'

import useForm from '@shared/composables/useForm'

import env from '@shared/env.js'
import howDidYouHearAboutUsOptionsConfig, {
  mskEuOptions,
} from '@shared/config/how-did-you-hear-about-us-options.js'
import { getTimezone } from '@shared/utils'
import Tracker from '@shared/Tracker'
import { isFromApp } from '@shared/helpers/isFromApp'
import defineLanguage from '@shared/utils/defineLanguage'

import BaseButton from '@shared/components/BaseButton.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import TextField from '@shared/components/form/TextField.vue'
import PasswordField from '@shared/components/form/PasswordField.vue'
import SelectField from '@shared/components/form/SelectField.vue'
import DocumentViewer from '@shared/components/DocumentViewer.vue'
import assembleKaiaAppDeepLink from '@shared/helpers/assembleKaiaAppDeepLink.js'
import useFeatureFlagStore from '@shared/stores/useFeatureFlagStore.ts'
import useSharedStore from '@shared/stores/useSharedStore.js'
import { submitSelfTestAnswers } from '@shared/modules/assessment/submitSelfTest.js'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import TrustSignal from '@shared/components/TrustSignal.vue'
import mdrIcon from '@shared/assets/img/icon_mdr.svg'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
import * as apiRequest from '@shared/api-endpoints'

const { progress } = useOnboardingProgress()
const { navigateNext, navigateBack, canGoBack } = useNavigation(ROUTE.SIGN_UP)

// import accessories
const userStore = useUserStore()
const euStore = useEuStore()
const sharedStore = useSharedStore()
const featureFlagStore = useFeatureFlagStore()
const router = useRouter()
const { t } = useI18n()

// define variables and feature toggles
const country = computed(() => userStore.country)
const lang = computed(() => userStore.lang)
const user = computed(() => userStore.user)
const formData = computed(() => euStore.formData)

const showLoginButton = ref(false) // toggle display of login button
const errorMessage = ref({ show: false, title: '', text: '' }) // error message that comes from backend or custom error

const title = computed(() =>
  featureFlagStore.flags.webOptimizedFunnel
    ? t('eu__signup__account_header_web')
    : t('eu__signup__account_header'),
)

const consentFootnote = computed(() =>
  t(
    featureFlagStore.flags.webOptimizedFunnel
      ? 'eu__signup__consent_footnote_web'
      : 'eu__signup__consent_footnote',
    { privacy_web: staticLinks.value.privacy_web },
  ),
)

const staticLinks = computed(() => {
  const base = env.VITE_PUBLIC_PATH
  const documentLang = defineLanguage(lang.value)

  return {
    contact: `${base}static/${documentLang}/contact/index.html`,
    privacy_web: `${base}static/${documentLang}/privacy-website/index.html`,
  }
})
const form = ref({
  firstName: formData.value.firstName,
  email: formData.value.email,
  password: formData.value.password,
  howDidYouHearAboutUs: formData.value.howDidYouHearAboutUs,
})

watchEffect(() => {
  euStore.updateFormData({ ...form.value })
})

watchEffect(() => {
  euStore.updateHowDidYouHearAboutUs(form.value.howDidYouHearAboutUs)
})

const formConfig = computed(() => ({
  ...(featureFlagStore.flags.webOptimizedFunnel
    ? {}
    : { firstName: { validations: ['required'] } }),
  email: { validations: ['required', 'email'] },
  password: { validations: ['required', 'password'] },
  howDidYouHearAboutUs: { validations: [] },
}))

const { fieldAttrs, submitAttrs } = useForm(form, formConfig, {
  onSubmit: async () => {
    showLoginButton.value = false
    hideErrorMessage()

    if (!user.value) {
      // should user be in the store, we don't try to sign up again but go right to the onSuccess callback
      await signUp()
    }

    if (featureFlagStore.flags.webOptimizedFunnel) {
      try {
        await submitSelfTestAnswers('preSignup', { partial: true })
      } catch (error) {
        console.error('Failed to submit pre-signup assessment answers:', error)
      }
    }
  },
  onSuccess: () => {
    navigateNext()
  },
  onError: async (e) => {
    showSignUpErrorMessage(e)
  },
})

const redirectToLogin = async () => {
  window.location.redirectToDeeplink(
    await assembleKaiaAppDeepLink({
      path: 'goto/screen',
      query: { target: 'login' },
    }),
  )
}

const trackSignupCompletedInMatomo = () => {
  // Virtual page view anchors the exact signup moment for Matomo↔user matching in the warehouse.
  const { href } = router.resolve({ path: '/signup-completed' })
  window._paq?.push(['setCustomUrl', href])
  window._paq?.push(['trackPageView'])
}

// display custom error message in case server returns error that is not about any field on the form
const showSignUpErrorMessage = (error) => {
  if (!error || !error.response) {
    throw error
  }

  hideErrorMessage()

  switch (error.response.status) {
    case 400:
      if (error.response?.data?.error === 'FIRST_NAME_CONTAINS_URL_ERROR') {
        showErrorMessage({
          title: t('generic__error_title'),
          text: t('eu__sign_up__first_name__error__special_chars', {
            email: env.VITE_SUPPORT_EMAIL,
          }),
        })
      } else {
        showErrorMessage({
          title: t('generic__error_title'),
          text: t('generic__error_message', {
            email: env.VITE_SUPPORT_EMAIL,
          }),
        })
      }
      break

    case 403:
      showLoginButton.value = true
      document.documentElement.scrollTop = 0
      break

    default:
      showErrorMessage({
        title: t('generic__error_title'),
        text: t('generic__error_message', {
          email: env.VITE_SUPPORT_EMAIL,
        }),
      })
  }
}

const signUp = () => {
  const { acceptedMarketing, acceptedWebTracking } = Tracker.getSignupConsents()

  return apiRequest
    .signUp({
      email: form.value.email,
      password: form.value.password,
      firstName: featureFlagStore.flags.webOptimizedFunnel
        ? euStore.formData.firstName
        : form.value.firstName,
      language: lang.value,
      country: country.value,
      timezone: getTimezone(),
      acceptedMarketing,
      acceptedWebTracking,
      fromApp: isFromApp(),
      referrer: sharedStore.initialQuery.referrer,
      signupContext: euStore.signupContext,
    })
    .then(
      () => {
        if (user.value) {
          trackSignupCompletedInMatomo()
          Tracker.trackKaiaEvent({
            event_name: 'c.lfcycl.create_account',
            action: 'view',
            object_type: 'screen',
            screen_name: 'signup_registration',
          })
          return
        }

        Tracker.trackKaiaEvent(
          {
            event_name: 'send_error',
            action: 'view',
            object_type: 'screen',
            screen_name: 'signup_registration',
          },
          {
            error_message: 'no user object after signup',
          },
        )

        throw new Error('no user object after signup')
      },
      (error) => {
        Tracker.trackKaiaEvent(
          {
            event_name: 'send_error',
            action: 'view',
            object_type: 'screen',
            screen_name: 'signup_registration',
          },
          {
            error_message:
              (error && error.response && error.response.status) ||
              t('generic__error_title'),
          },
        )

        throw error
      },
    )
}

const appName = computed(() => {
  return t('eu__generic__app')
})

const alreadyHaveAccount = isFromApp()
  ? t('eu__signup__already_have_account_in_app')
  : t('eu__signup__already_have_account', { app_name: appName.value })

const alreadyHaveAccountButton = isFromApp()
  ? t('generic_login')
  : t('generic__download_app')

const howDidYouHearAboutUsOptions = computed(() => {
  const options = env.isMskEu ? mskEuOptions : howDidYouHearAboutUsOptionsConfig
  return options.map(({ value, name }) => ({
    text: t(name),
    attrs: { value },
  }))
})

// hide error message that we received from the backend
const hideErrorMessage = () => {
  errorMessage.value.show = false
  errorMessage.value.title = ''
  errorMessage.value.text = ''
}

// show error message that we received from the backend
const showErrorMessage = ({ title = '', text = '' }) => {
  errorMessage.value.show = true
  errorMessage.value.title = title
  errorMessage.value.text = text
}
</script>
<style lang="scss" scoped>
.legal-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: $kds-space-1x;
  color: $kds-color-text-tertiary;

  a {
    // keep the tap target comfortably tappable on mobile web
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 0 $kds-space-1x;
    color: inherit;
    text-decoration: underline;
    font-weight: $kds-typography-font-weight-medium;
  }
}
</style>
